// src/storage/usePacksBridge.js -- Self-Serve Packs Slice 1.
//
// Loads the LIVE pack catalog from Firestore (/packs) into state.packs, so admin-created
// packs appear in the storefront without a code change/deploy. Mirrors
// useSiteContentBridge: PUBLIC read, runs once on mount, FIREBASE/EMULATOR mode only.
// Mock mode / no-db / read FAILURE -> does nothing, so state.packs stays as-is (empty by
// default -> empty storefront; no bundled demo packs). A SUCCESSFUL read publishes the live
// catalog even when it is EMPTY (admin is the source of truth -> a genuinely empty storefront).
//
// It adapts each /packs doc into the storefront pack-card shape so all state.packs consumers
// keep working unchanged. Only published + coming_soon + publicVisible packs are surfaced
// (draft/archived are hidden).
import { useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db, AUTH_MODE } from '../auth/firebase.js';
import { normalizePackType } from '../admin/packType.js';

// Firestore lifecycle status -> storefront catalog status (the storefront uses hyphenated
// 'available'/'coming-soon'; the live doc uses the PACK_STATUS enum).
const STATUS_MAP = { published: 'available', coming_soon: 'coming-soon' };

// Adapt one /packs doc to a storefront pack-card. Returns null for packs that should
// not appear in the storefront (draft/archived, or not publicVisible).
function adaptPack(id, d) {
    const status = STATUS_MAP[d.status];
    if (!status || d.publicVisible !== true) return null;

    const md = d.metadata || {};
    const amount = d.pricing && d.pricing.HKD && d.pricing.HKD.amount;
    const price = (Number.isInteger(amount) && amount > 0) ? Math.round(amount / 100) : null; // null = unpriced (no figure shown)
    const freeItems = Array.isArray(d.freeItemIds) ? d.freeItemIds.length : 0;

    return {
        id,
        taster: d.taster || null,
        status,
        price,
        billing: 'Annual',          // launch default (single annual plan)
        autoRenewInterval: 'yearly',
        freeTrialDays: 0,
        paper: md.examBoard || '',
        label: md.title || id,
        subLabel: md.subtitle || '',
        spineLabel: md.spineLabel || '', // optional short bookshelf-spine name (blank = use label)
        description: md.description || '',
        items: 0,                   // total-item count is not on the pack doc; cosmetic only
        freeItems,                  // derived from freeItemIds (CF-maintained free-item list)
        theme: md.theme || 'violet',
        // library-2c: optional per-pack cover colour. Overrides `theme` in
        // getPackTheme; null/absent (the normal case) leaves the swatch in charge.
        coverColor: typeof md.coverColor === 'string' ? md.coverColor : null,
        packType: normalizePackType(md.packType),
        // Bundle clarity: surface the server's purchase gate so the storefront
        // card can branch. Only an EXPLICIT purchaseEnabled=false is treated as
        // not-purchasable (undefined/true stay purchasable -> unchanged card),
        // matching the CF gate direction without relabeling legacy packs.
        purchasable: d.purchaseEnabled !== false,
        // Series/Bundle S1: member pack ids ride along so the series card can list
        // what the bundle includes. Empty for ordinary packs. (Written server-side
        // from S2 on; until then only hand-seeded emulator docs carry it.)
        memberPackIds: Array.isArray(d.memberPackIds) ? d.memberPackIds.filter((x) => typeof x === 'string') : []
        // cover/typography intentionally omitted -> the card helpers
        // (getPackTheme/getPackTypography) supply theme-based defaults, never blank.
    };
}

export function usePacksBridge(state) {
    useEffect(() => {
        if (AUTH_MODE === 'mock' || !db) return; // inert in mock: empty storefront stands
        let cancelled = false;
        (async () => {
            try {
                const snap = await getDocs(collection(db, 'packs'));
                const entries = [];
                snap.forEach((docSnap) => {
                    const data = docSnap.data() || {};
                    const card = adaptPack(docSnap.id, data);
                    if (card) entries.push({ order: Number.isInteger(data.displayOrder) ? data.displayOrder : 999, card });
                });
                if (cancelled) return;
                // SUCCESS (getDocs did not throw): admin is the source of truth -- publish
                // the live catalog even when it is EMPTY (genuinely no published packs ->
                // an empty storefront). A FAILED read throws to the catch below, where the
                // existing state.packs is left untouched.
                entries.sort((a, b) => (a.order - b.order) || a.card.label.localeCompare(b.card.label));
                state.setPacks(entries.map((e) => e.card));
            } catch (e) {
                // Read FAILED (permission/network) -> leave state.packs as-is. Do not blank the
                // storefront over a transient/rules error.
                console.error('usePacksBridge:', e);
            } finally {
                // Clear the loading flag once the read resolves either way, so the
                // storefront stops showing the loading state (no static flash).
                if (!cancelled && state.setPacksLoading) state.setPacksLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // once on mount
}

export default usePacksBridge;
